import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../api/apiClient';

/**
 * Fear & Greed gauge — semicircular dial with needle, value 0–100,
 * coloured by region (Extreme Fear → Extreme Greed). Source endpoint
 * is provided by the parent so the same component renders both the
 * stock (CNN) and crypto (alternative.me) variants.
 */
// 5 segments around the 180° arc. Each visually occupies 1/5 of the arc.
// Centre (200, 220), radius 150, viewBox 400×270. Labels sit at radius 188
// (well outside the 28-px-thick arc) so they never overlap with the needle.
const SEGMENTS = [
  {
    name: 'Extreme Fear', matchMin: 0,  matchMax: 24,  color: '#dc2626',
    path: 'M 50 220 A 150 150 0 0 1 78.65 131.8',
    labelX: 22,  labelY: 165, labelRotate: -72,
  },
  {
    name: 'Fear',          matchMin: 25, matchMax: 44,  color: '#f97316',
    path: 'M 78.65 131.8 A 150 150 0 0 1 153.65 77.35',
    labelX: 90,  labelY: 70,  labelRotate: -36,
  },
  {
    name: 'Neutral',       matchMin: 45, matchMax: 55,  color: '#eab308',
    path: 'M 153.65 77.35 A 150 150 0 0 1 246.35 77.35',
    labelX: 200, labelY: 35,  labelRotate: 0,
  },
  {
    name: 'Greed',         matchMin: 56, matchMax: 75,  color: '#84cc16',
    path: 'M 246.35 77.35 A 150 150 0 0 1 321.35 131.8',
    labelX: 310, labelY: 70,  labelRotate: 36,
  },
  {
    name: 'Extreme Greed', matchMin: 76, matchMax: 100, color: '#16a34a',
    path: 'M 321.35 131.8 A 150 150 0 0 1 350 220',
    labelX: 378, labelY: 165, labelRotate: 72,
  },
];

// Per-source methodology copy shown under the gauge.
const METHODOLOGY = {
  'CNN Business': {
    intro: 'CNN Business calculates the Stock Fear & Greed Index from seven equally weighted indicators of US equity market behaviour. Each runs on a 0–100 scale and the average is the headline value.',
    factors: [
      { name: 'Stock Price Momentum',  detail: 'S&P 500 vs its 125-day moving average.' },
      { name: 'Stock Price Strength',  detail: 'Number of NYSE stocks hitting 52-week highs vs lows.' },
      { name: 'Stock Price Breadth',   detail: 'Trading volume in rising vs declining stocks (McClellan Volume Summation).' },
      { name: 'Put / Call Options',    detail: '5-day average ratio of put to call options traded.' },
      { name: 'Market Volatility',     detail: 'VIX vs its 50-day moving average.' },
      { name: 'Safe Haven Demand',     detail: 'Stock returns vs Treasury bond returns over 20 days.' },
      { name: 'Junk Bond Demand',      detail: 'Yield spread between investment-grade and junk bonds.' },
    ],
  },
  'CoinMarketCap': {
    intro: 'CoinMarketCap calculates the Crypto Fear & Greed Index from market data, on-chain activity and social signals across the crypto market. Higher values mean traders are getting greedy; lower values mean fear is dominating.',
    factors: [
      { name: 'Price Momentum',     detail: 'Current price relative to recent averages — sharp drops push the index toward fear.' },
      { name: 'Volatility',         detail: '30-day and 90-day volatility of Bitcoin’s price compared to its long-term average.' },
      { name: 'Trading Volume',     detail: 'Recent buy volume vs the prior month — strong demand pushes toward greed.' },
      { name: 'Social Media',       detail: 'Posting rate and engagement around major coins across social platforms.' },
      { name: 'Bitcoin Dominance',  detail: 'BTC share of total crypto market cap — rising dominance often signals fear in altcoins.' },
      { name: 'Google Trends',      detail: 'Search interest for crypto-related queries.' },
    ],
  },
};

export default function FearGreedGauge({ endpoint, title = 'Fear & Greed Index' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiFetch(endpoint)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [endpoint]);

  if (loading) {
    return (
      <div style={s.wrap}>
        <div style={s.header}>
          <h3 style={s.title}>{title}</h3>
        </div>
        <div style={s.skeleton} />
      </div>
    );
  }

  if (!data?.available) return null;

  const value = Math.max(0, Math.min(100, Math.round(Number(data.value || 0))));
  const classification = data.classification || classifyValue(value);
  const colour = colourForValue(value);
  const angle = (value / 100) * 180 - 90; // -90° (left) → +90° (right)

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <h3 style={s.title}>{title}</h3>
        {data.source && <span style={s.source}>{data.source}</span>}
      </div>

      <div style={s.body}>
        {/* Segmented gauge — segments to the left of the value (and the one
            containing it) are filled with their own colour; segments after
            are faded grey. Acts like a progress fill from 0 to current value. */}
        <div style={s.gaugeWrap}>
          <svg viewBox="0 0 400 285" style={s.svg}>
            {SEGMENTS.map((seg) => {
              const past   = value > seg.matchMax;       // before the active one
              const active = value >= seg.matchMin && value <= seg.matchMax;
              const filled = past || active;
              return (
                <React.Fragment key={seg.name}>
                  <path
                    d={seg.path}
                    stroke={filled ? seg.color : 'var(--border)'}
                    strokeWidth="28"
                    fill="none"
                    opacity={filled ? (active ? 1 : 0.95) : 0.5}
                  />
                  <text
                    x={seg.labelX}
                    y={seg.labelY}
                    textAnchor="middle"
                    transform={`rotate(${seg.labelRotate} ${seg.labelX} ${seg.labelY})`}
                    style={{
                      ...s.segmentLabel,
                      fill: active ? seg.color : 'var(--text-secondary)',
                      fontWeight: active ? 800 : 600,
                      opacity: active ? 1 : 0.75,
                    }}
                  >
                    {seg.name.toUpperCase()}
                  </text>
                </React.Fragment>
              );
            })}

            {/* Needle */}
            <g transform={`rotate(${angle} 200 220)`}>
              <line x1="200" y1="220" x2="200" y2="80"
                    stroke="var(--text-primary)" strokeWidth="3.5" strokeLinecap="round" />
              <circle cx="200" cy="220" r="11" fill="var(--text-primary)" />
              <circle cx="200" cy="220" r="5"  fill="var(--surface-strong)" />
            </g>

            {/* Value under the needle pivot — sits below the pivot circle */}
            <text x="200" y="272" textAnchor="middle"
                  style={{ ...s.value, fill: colour }}>{value}</text>

            {/* 0 / 100 endpoints aligned with value baseline */}
            <text x="50"  y="272" textAnchor="middle" style={s.scaleText}>0</text>
            <text x="350" y="272" textAnchor="middle" style={s.scaleText}>100</text>
          </svg>
        </div>
        <div style={s.classificationWrap}>
          <span style={{ ...s.classification, color: colour }}>{classification}</span>
        </div>

        {(data.previousClose != null || data.previous1Week != null || data.previous1Month != null) && (
          <div style={s.historyRow}>
            {data.previousClose != null && <HistoryCell label="Yesterday" value={data.previousClose} />}
            {data.previous1Week  != null && <HistoryCell label="1 week ago" value={data.previous1Week} />}
            {data.previous1Month != null && <HistoryCell label="1 month ago" value={data.previous1Month} />}
            {data.previous1Year  != null && <HistoryCell label="1 year ago" value={data.previous1Year} />}
          </div>
        )}

        {/* Methodology / what drives the score ─────────────────────────── */}
        {data.source && METHODOLOGY[data.source] && (
          <div style={s.methodology}>
            <div style={s.methodologyTitle}>How is this calculated?</div>
            <p style={s.methodologyIntro}>{METHODOLOGY[data.source].intro}</p>
            <ul style={s.factorList}>
              {METHODOLOGY[data.source].factors.map(f => (
                <li key={f.name} style={s.factorItem}>
                  <span style={s.factorName}>{f.name}</span>
                  <span style={s.factorDetail}> — {f.detail}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function HistoryCell({ label, value }) {
  const v = Math.round(value);
  return (
    <div style={s.histCell}>
      <div style={s.histLabel}>{label}</div>
      <div style={{ ...s.histValue, color: colourForValue(v) }}>{v}</div>
      <div style={s.histTag}>{classifyValue(v)}</div>
    </div>
  );
}

function classifyValue(v) {
  if (v < 25)  return 'Extreme Fear';
  if (v < 45)  return 'Fear';
  if (v <= 55) return 'Neutral';
  if (v <= 75) return 'Greed';
  return 'Extreme Greed';
}

function colourForValue(v) {
  if (v < 25)  return '#dc2626';
  if (v < 45)  return '#f97316';
  if (v <= 55) return '#facc15';
  if (v <= 75) return '#84cc16';
  return '#16a34a';
}

const s = {
  wrap: {
    background: 'var(--surface-strong)',
    border: '1px solid var(--border)',
    borderRadius: '14px',
    padding: '18px 22px',
    boxShadow: 'var(--shadow)',
    marginBottom: '24px',
  },
  header: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '10px' },
  title: { margin: 0, fontSize: '16px', fontWeight: 800, color: 'var(--text-primary)' },
  source: { fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.4px' },
  body: { display: 'flex', flexDirection: 'column', gap: '12px' },

  gaugeWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  svg: { width: '100%', maxWidth: '480px', height: 'auto' },
  scaleText: { fill: 'var(--text-secondary)', fontSize: '13px', fontWeight: 700 },
  segmentLabel: {
    fontSize: '9.5px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  // value is inside the SVG → use `fill`
  value: { fontSize: '42px', fontWeight: 900 },
  // classification sits below the SVG as plain DOM
  classificationWrap: { textAlign: 'center', marginTop: '-8px' },
  classification: { fontSize: '20px', fontWeight: 800, letterSpacing: '0.3px' },

  historyRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
    gap: '8px',
    paddingTop: '10px',
    borderTop: '1px solid var(--border)',
  },
  histCell: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '6px 4px',
    background: 'var(--surface)',
    borderRadius: '8px',
  },
  histLabel: { fontSize: '10px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.3px' },
  histValue: { fontSize: '20px', fontWeight: 800, lineHeight: 1.2 },
  histTag: { fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 },

  skeleton: { height: '180px', borderRadius: '8px', background: 'var(--surface)', animation: 'pulse 1.4s ease-in-out infinite' },

  methodology: {
    marginTop: '6px',
    paddingTop: '14px',
    borderTop: '1px solid var(--border)',
  },
  methodologyTitle: {
    fontSize: '13px', fontWeight: 800, letterSpacing: '0.3px',
    color: 'var(--text-primary)', marginBottom: '6px',
    textTransform: 'uppercase',
  },
  methodologyIntro: {
    margin: '0 0 10px', fontSize: '13px', lineHeight: 1.55,
    color: 'var(--text-secondary)',
  },
  factorList: {
    margin: 0, padding: 0, listStyle: 'none',
    display: 'flex', flexDirection: 'column', gap: '6px',
  },
  factorItem: {
    fontSize: '13px', lineHeight: 1.5,
    color: 'var(--text-secondary)',
    paddingLeft: '14px', position: 'relative',
  },
  factorName: { color: 'var(--text-primary)', fontWeight: 700 },
  factorDetail: {},
};
